import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Plus, Sparkles, Loader2, Upload, Download, Save, BarChart3, Wand2,
  LayoutDashboard, Trash2, ChevronRight, Calculator, Sigma, ChevronLeft,
  Mic, MicOff, FunctionSquare, Brush, TrendingUp, FileText, X, LayoutTemplate, Check,
} from "lucide-react";
import { docsApi, aiQuick } from "../api";

const KIND = "excel";
const ROWS = 100;
const COLS = 26;
const colLetter = (i) => {
  if (i < 26) return String.fromCharCode(65 + i);
  return String.fromCharCode(64 + Math.floor(i / 26)) + String.fromCharCode(65 + (i % 26));
};
const blank = () => Array.from({ length: ROWS }, () => Array.from({ length: COLS }, () => ""));

const CHART_COLORS = ["#a78bfa", "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899", "#84cc16"];

// ───────────────────────────────────── AI Brain ─────────────────────────────────────
// Detect headers, find a column by name (case-insensitive substring), aggregate.
const detectGrid = (grid) => {
  // Header row = first row if it has at least 2 non-empty string cells AND
  // the values below are mostly numeric.
  const firstRow = grid[0] || [];
  const stringHeaderCount = firstRow.filter((c) => typeof c === "string" && c.trim() && isNaN(parseFloat(c))).length;
  const hasHeaders = stringHeaderCount >= 2;
  const headerRow = hasHeaders ? 0 : -1;
  const dataStart = hasHeaders ? 1 : 0;
  const headers = hasHeaders
    ? firstRow.map((c, i) => (c && String(c).trim()) || colLetter(i))
    : Array.from({ length: COLS }, (_, i) => colLetter(i));
  // Determine which columns are numeric (>= 60% of non-empty cells parse as numbers).
  const colTypes = headers.map((_, c) => {
    let nums = 0, total = 0;
    for (let r = dataStart; r < grid.length; r++) {
      const v = grid[r]?.[c];
      if (v === "" || v == null) continue;
      total++;
      if (!isNaN(parseFloat(v))) nums++;
    }
    return total > 0 && nums / total >= 0.6 ? "number" : "text";
  });
  // Used row range = rows where at least one cell is non-empty.
  const usedRows = [];
  for (let r = dataStart; r < grid.length; r++) {
    if ((grid[r] || []).some((c) => c !== "" && c != null)) usedRows.push(r);
  }
  return { hasHeaders, headerRow, dataStart, headers, colTypes, usedRows };
};

const columnNumbers = (grid, colIdx, meta) => {
  const out = [];
  for (const r of meta.usedRows) {
    const v = grid[r]?.[colIdx];
    const n = parseFloat(v);
    if (!isNaN(n)) out.push(n);
  }
  return out;
};

const findColumn = (query, meta) => {
  const q = (query || "").toLowerCase().trim();
  if (!q) return -1;
  // Exact then substring
  let idx = meta.headers.findIndex((h) => String(h).toLowerCase() === q);
  if (idx >= 0) return idx;
  idx = meta.headers.findIndex((h) => String(h).toLowerCase().includes(q));
  if (idx >= 0) return idx;
  // Tokens: pick column whose name shares the most tokens with the question.
  const tokens = q.split(/\s+/).filter((t) => t.length >= 3);
  let best = -1, bestScore = 0;
  meta.headers.forEach((h, i) => {
    const low = String(h).toLowerCase();
    const score = tokens.reduce((s, t) => s + (low.includes(t) ? 1 : 0), 0);
    if (score > bestScore) { bestScore = score; best = i; }
  });
  return bestScore > 0 ? best : -1;
};

// Match a NL question to a deterministic action. Returns { kind, ... } or null
// to defer to LLM.
const parseQuestion = (q, meta) => {
  const t = q.toLowerCase().trim();
  // Operation keyword
  const opMatch = t.match(/\b(total|sum|average|avg|mean|max|maximum|min|minimum|count|how many)\b/);
  const op = opMatch ? opMatch[1] : null;
  // Chart keyword
  const chart = /\b(chart|graph|plot|bar|line|pie|visuali[sz]e)\b/.test(t);
  // Find target column (if mentioned)
  const colIdx = findColumn(q, meta);
  if (chart) {
    // Pick the first numeric column if user didn't name one.
    const numericCols = meta.colTypes.map((t, i) => t === "number" ? i : -1).filter((i) => i >= 0);
    const target = colIdx >= 0 && meta.colTypes[colIdx] === "number" ? colIdx : numericCols[0];
    if (target == null) return null;
    // X axis = first text column, fallback to row indices.
    const xCol = meta.colTypes.findIndex((t) => t === "text");
    const chartType = /\bline\b/.test(t) ? "line" : /\bpie\b/.test(t) ? "pie" : "bar";
    return { kind: "chart", chartType, valueCol: target, labelCol: xCol >= 0 ? xCol : -1 };
  }
  if (op && colIdx >= 0) {
    return { kind: "agg", op, col: colIdx };
  }
  if (op && !colIdx) {
    // No column named — use the first numeric column.
    const target = meta.colTypes.findIndex((t) => t === "number");
    if (target >= 0) return { kind: "agg", op, col: target };
  }
  return null;
};

const runAction = (action, grid, meta) => {
  if (action.kind === "agg") {
    const nums = columnNumbers(grid, action.col, meta);
    if (nums.length === 0) return { text: `No numeric data in "${meta.headers[action.col]}".` };
    const op = action.op;
    let value, label;
    if (op === "total" || op === "sum") { value = nums.reduce((s, n) => s + n, 0); label = "Total"; }
    else if (op === "average" || op === "avg" || op === "mean") { value = nums.reduce((s, n) => s + n, 0) / nums.length; label = "Average"; }
    else if (op === "max" || op === "maximum") { value = Math.max(...nums); label = "Max"; }
    else if (op === "min" || op === "minimum") { value = Math.min(...nums); label = "Min"; }
    else { value = nums.length; label = "Count"; }
    return {
      text: `${label} of "${meta.headers[action.col]}" = ${typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value} (${nums.length} rows)`,
      result: { label, value, col: meta.headers[action.col], rows: nums.length },
    };
  }
  if (action.kind === "chart") {
    const data = meta.usedRows.map((r, idx) => ({
      name: action.labelCol >= 0 ? String(grid[r]?.[action.labelCol] || `Row ${idx + 1}`) : `Row ${idx + 1}`,
      value: parseFloat(grid[r]?.[action.valueCol]) || 0,
    }));
    return {
      text: `Generated ${action.chartType} chart of "${meta.headers[action.valueCol]}".`,
      chart: { type: action.chartType, data, valueLabel: meta.headers[action.valueCol] },
    };
  }
  return null;
};

// ───────────────────────────────────── Excel App ─────────────────────────────────────
export default function Excel() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [chat, setChat] = useState([]);          // { role: "user"|"ai", text, chart? }
  const [aiInput, setAiInput] = useState("");
  const [selectedCell, setSelectedCell] = useState(null);  // { r, c }
  const [dashOpen, setDashOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Phase 2: voice + formula generator
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const [overlay, setOverlay] = useState(null); // null | 'formula' | 'templates' | 'clean' | 'forecast' | 'saveas'
  const [formulaInput, setFormulaInput] = useState("");
  const [formulaResult, setFormulaResult] = useState("");
  const [formulaBusy, setFormulaBusy] = useState(false);
  const [cleanReport, setCleanReport] = useState(null); // { duplicates, trimmed, beforeRows, afterRows }
  const [forecastResult, setForecastResult] = useState(null); // { col, history, predicted }
  // MS Office-style save state
  const [saveState, setSaveState] = useState("saved"); // 'idle' | 'saving' | 'saved' | 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveAsName, setSaveAsName] = useState("");
  const fileInputRef = useRef(null);

  const meta = useMemo(() => active ? detectGrid(active.content) : null, [active?.content]);

  const load = async () => {
    const list = await docsApi.list(KIND);
    setDocs(list);
    if (!active && list[0]) setActive({ ...list[0], content: normalize(list[0].content) });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Auto-save debounce (with state tracking)
  useEffect(() => {
    if (!active) return;
    setSaveState("idle");
    const t = setTimeout(async () => {
      setSaveState("saving");
      try {
        const d = await docsApi.update(active.id, { kind: KIND, title: active.title, content: active.content });
        setDocs((ds) => ds.map((x) => x.id === d.id ? d : x));
        setSaveState("saved");
        setLastSavedAt(new Date());
      } catch {
        setSaveState("error");
      }
    }, 800);
    return () => clearTimeout(t);
  }, [active?.title, active?.content]);

  // Manual Save (Ctrl/Cmd + S) — saves immediately to HEXA cloud.
  const saveNow = async () => {
    if (!active) return;
    setSaveState("saving");
    try {
      const d = await docsApi.update(active.id, { kind: KIND, title: active.title, content: active.content });
      setDocs((ds) => ds.map((x) => x.id === d.id ? d : x));
      setSaveState("saved");
      setLastSavedAt(new Date());
    } catch {
      setSaveState("error");
    }
  };

  // Save As — creates a NEW sheet (a copy) in HEXA cloud with the chosen name.
  const saveAsNewSheet = async (newName) => {
    if (!active) return;
    const title = (newName || `${active.title || "Sheet"} (copy)`).trim() || "Untitled Sheet";
    setSaveState("saving");
    try {
      const d = await docsApi.create({ kind: KIND, title, content: active.content });
      setDocs((ds) => [d, ...ds]);
      setActive({ ...d, content: normalize(d.content) });
      setChat([]);
      setSaveState("saved");
      setLastSavedAt(new Date());
      setOverlay(null);
      setSaveAsName("");
    } catch {
      setSaveState("error");
    }
  };

  // Download as CSV (Save As → .csv format)
  const downloadCsv = () => {
    if (!active) return;
    const trimmed = active.content
      .map((row) => row.slice(0, COLS))
      .filter((row) => row.some((c) => c !== "" && c != null));
    const ws = XLSX.utils.aoa_to_sheet(trimmed.length ? trimmed : [[""]]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active.title || "Sheet"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Ctrl/Cmd + S → Save · Ctrl/Cmd + Shift + S → Save As
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        if (e.shiftKey) {
          setSaveAsName(active?.title ? `${active.title} (copy)` : "Untitled Sheet");
          setOverlay("saveas");
        } else {
          saveNow();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [active]);

  const normalize = (c) => {
    if (Array.isArray(c) && c.length === ROWS && (c[0] || []).length === COLS) return c;
    const g = blank();
    if (Array.isArray(c)) for (let r = 0; r < Math.min(ROWS, c.length); r++) {
      for (let cc = 0; cc < Math.min(COLS, (c[r] || []).length); cc++) g[r][cc] = c[r][cc];
    }
    return g;
  };

  const create = async () => {
    const d = await docsApi.create({ kind: KIND, title: "New Sheet", content: blank() });
    setDocs([d, ...docs]);
    setActive({ ...d, content: blank() });
    setChat([]);
  };

  const removeSheet = async (id) => {
    if (!window.confirm("Delete this sheet?")) return;
    await docsApi.remove(id);
    const remaining = docs.filter((d) => d.id !== id);
    setDocs(remaining);
    if (active?.id === id) {
      setActive(remaining[0] ? { ...remaining[0], content: normalize(remaining[0].content) } : null);
    }
  };

  const setCell = (r, c, v) => {
    const next = active.content.map((row) => row.slice());
    next[r][c] = v;
    setActive({ ...active, content: next });
  };

  // ── Formula engine (kept simple — SUM/AVG/MIN/MAX/COUNT + arithmetic) ──
  const evalCell = (val) => {
    if (typeof val !== "string" || !val.startsWith("=")) return val;
    try {
      const expr = val.slice(1).toUpperCase();
      const refToVal = (ref) => {
        const m = ref.match(/^([A-Z]+)(\d+)$/);
        if (!m) return 0;
        const c = m[1].length === 1 ? m[1].charCodeAt(0) - 65 : (m[1].charCodeAt(0) - 64) * 26 + (m[1].charCodeAt(1) - 65);
        const r = parseInt(m[2], 10) - 1;
        const v = active.content[r]?.[c];
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
      };
      const range = (start, end) => {
        const a = start.match(/^([A-Z]+)(\d+)$/);
        const b = end.match(/^([A-Z]+)(\d+)$/);
        if (!a || !b) return [];
        const colIdx = (s) => s.length === 1 ? s.charCodeAt(0) - 65 : (s.charCodeAt(0) - 64) * 26 + (s.charCodeAt(1) - 65);
        const c1 = colIdx(a[1]), c2 = colIdx(b[1]);
        const r1 = parseInt(a[2], 10) - 1, r2 = parseInt(b[2], 10) - 1;
        const vals = [];
        for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
          const v = parseFloat(active.content[r]?.[c]);
          if (!isNaN(v)) vals.push(v);
        }
        return vals;
      };
      const fn = (name, args) => {
        const nums = args.flatMap((a) => a.includes(":") ? range(a.split(":")[0], a.split(":")[1]) : [refToVal(a)]);
        if (name === "SUM") return nums.reduce((s, n) => s + n, 0);
        if (name === "AVG" || name === "AVERAGE") return nums.length ? nums.reduce((s, n) => s + n, 0) / nums.length : 0;
        if (name === "MAX") return Math.max(...nums);
        if (name === "MIN") return Math.min(...nums);
        if (name === "COUNT") return nums.length;
        return 0;
      };
      const m = expr.match(/^(SUM|AVG|AVERAGE|MAX|MIN|COUNT)\(([^)]+)\)$/);
      if (m) return fn(m[1], m[2].split(","));
      const replaced = expr.replace(/[A-Z]+\d+/g, (ref) => refToVal(ref).toString());
      // eslint-disable-next-line no-new-func
      return Function(`"use strict"; return (${replaced})`)();
    } catch { return "#ERR"; }
  };

  // ── Upload .xlsx / .csv via SheetJS (client-side) ──
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const sheetName = wb.SheetNames[0];
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
    const g = blank();
    for (let r = 0; r < Math.min(ROWS, rows.length); r++) {
      const row = rows[r] || [];
      for (let c = 0; c < Math.min(COLS, row.length); c++) {
        const v = row[c];
        g[r][c] = v == null ? "" : (typeof v === "object" ? String(v) : v);
      }
    }
    const newDoc = await docsApi.create({ kind: KIND, title: file.name.replace(/\.[^.]+$/, ""), content: g });
    setDocs([newDoc, ...docs]);
    setActive({ ...newDoc, content: g });
    setChat([{ role: "ai", text: `Imported ${rows.length} rows × ${(rows[0] || []).length} cols from "${file.name}".` }]);
    e.target.value = "";
  };

  // ── Download as .xlsx ──
  const downloadXlsx = () => {
    if (!active) return;
    // Trim trailing empty rows / cols for a clean export.
    const trimmed = active.content
      .map((row) => row.slice(0, COLS))
      .filter((row) => row.some((c) => c !== "" && c != null));
    const ws = XLSX.utils.aoa_to_sheet(trimmed.length ? trimmed : [[""]]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${active.title || "Sheet"}.xlsx`);
  };

  // ── AI Brain chat ──
  const sendChat = async (questionRaw) => {
    const question = (questionRaw ?? aiInput).trim();
    if (!question || !active) return;
    setChat((c) => [...c, { role: "user", text: question }]);
    setAiInput("");
    setAiBusy(true);
    // 1) Try the deterministic intent parser.
    const action = parseQuestion(question, meta);
    if (action) {
      const result = runAction(action, active.content, meta);
      setChat((c) => [...c, { role: "ai", ...result }]);
      setAiBusy(false);
      return;
    }
    // 2) Fall back to Claude with a compact CSV snapshot.
    try {
      const csv = active.content
        .filter((row) => row.some((cell) => cell !== "" && cell != null))
        .slice(0, 60)
        .map((row) => row.join(","))
        .join("\n");
      const sys = "You are an AI data analyst. Reply in <=120 words. If asked for a number, output it on a separate line.";
      const r = await aiQuick(`Spreadsheet headers: ${meta?.headers.slice(0, 12).join(", ")}\nData (CSV, first 60 rows):\n${csv}\n\nQuestion: ${question}`, sys);
      setChat((c) => [...c, { role: "ai", text: r.text }]);
    } catch {
      setChat((c) => [...c, { role: "ai", text: "AI unavailable. Try a deterministic question like 'total <column>' or 'chart of <column>'." }]);
    }
    setAiBusy(false);
  };

  // ── Voice input on the chat box (Web Speech API) ──
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setChat((c) => [...c, { role: "ai", text: "Voice input not supported in this browser." }]); return; }
    if (listening) { try { recogRef.current?.stop(); } catch { /* ignore */ } setListening(false); return; }
    const r = new SR();
    r.lang = "en-US"; r.continuous = false; r.interimResults = true;
    let finalText = "";
    r.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t + " ";
        else interim += t;
      }
      setAiInput((finalText + interim).trim());
    };
    r.onerror = () => setListening(false);
    r.onend = () => {
      setListening(false);
      const q = finalText.trim();
      if (q) setTimeout(() => sendChat(q), 50);
    };
    recogRef.current = r;
    try { r.start(); setListening(true); } catch { /* ignore */ }
  };

  // ── Formula generator: NL → Excel formula via Claude ──
  const runFormulaGen = async () => {
    if (!formulaInput.trim()) return;
    setFormulaBusy(true);
    setFormulaResult("");
    try {
      const headerHint = meta?.headers.slice(0, 8).join(", ") || "";
      const sys = "You convert plain-English spreadsheet rules into a SINGLE Excel formula. Reply with ONLY the formula starting with '=', no prose, no markdown, no explanation. Use standard Excel functions (IF, SUMIF, COUNTIF, VLOOKUP, AVERAGE, etc.). Use A1-style references.";
      const prompt = `Sheet headers (left→right): ${headerHint}\n\nRule: ${formulaInput}\n\nFormula:`;
      const r = await aiQuick(prompt, sys);
      // Take first line that starts with =
      const line = (r.text || "").split("\n").map((l) => l.trim()).find((l) => l.startsWith("=")) || r.text;
      setFormulaResult(line.trim());
    } catch {
      setFormulaResult("AI unavailable — please try again.");
    }
    setFormulaBusy(false);
  };
  const applyFormulaToSelected = () => {
    if (!selectedCell || !formulaResult || !formulaResult.startsWith("=")) return;
    setCell(selectedCell.r, selectedCell.c, formulaResult);
    setOverlay(null);
    setFormulaResult("");
    setFormulaInput("");
  };

  // ── Templates gallery ──
  const TEMPLATES = [
    {
      id: "sales",
      title: "Sales Tracker",
      desc: "Track deals, owners, stage and revenue.",
      icon: TrendingUp,
      tint: "#22c55e",
      data: [
        ["Date", "Customer", "Owner", "Product", "Stage", "Amount"],
        ["2026-01-05", "Acme Corp", "Priya", "Pro Plan", "Closed Won", 12000],
        ["2026-01-12", "Globex", "Ravi", "Enterprise", "Closed Won", 48000],
        ["2026-01-18", "Initech", "Priya", "Starter", "Negotiation", 4500],
        ["2026-01-22", "Hooli", "Mei", "Pro Plan", "Closed Won", 12000],
        ["2026-02-01", "Wonka", "Ravi", "Enterprise", "Proposal", 36000],
        ["2026-02-09", "Stark Ind.", "Mei", "Pro Plan", "Closed Won", 14000],
        ["2026-02-15", "Wayne Co", "Priya", "Starter", "Closed Lost", 0],
        ["2026-02-23", "Pied Piper", "Ravi", "Pro Plan", "Closed Won", 12000],
      ],
    },
    {
      id: "expense",
      title: "Expense Manager",
      desc: "Monthly cash-out by category.",
      icon: Calculator,
      tint: "#f59e0b",
      data: [
        ["Date", "Category", "Vendor", "Description", "Amount", "Paid By"],
        ["2026-01-02", "Travel", "Uber", "Client visit", 42, "Card"],
        ["2026-01-05", "Software", "Figma", "Subscription", 144, "Card"],
        ["2026-01-09", "Meals", "Pizzeria", "Team lunch", 86, "Cash"],
        ["2026-01-14", "Office", "Staples", "Stationery", 124, "Card"],
        ["2026-01-20", "Travel", "Delta", "NYC trip", 380, "Card"],
        ["2026-01-25", "Software", "Notion", "Subscription", 96, "Card"],
        ["2026-02-03", "Meals", "Cafe", "Coffee", 18, "Cash"],
        ["2026-02-08", "Marketing", "Google Ads", "Campaign", 540, "Card"],
      ],
    },
    {
      id: "crm",
      title: "CRM",
      desc: "Contacts + last touchpoint + status.",
      icon: LayoutTemplate,
      tint: "#a78bfa",
      data: [
        ["Name", "Company", "Email", "Phone", "Status", "Last Touch", "Value"],
        ["Alice Chen", "Apex Labs", "alice@apex.io", "+1 415 555 0101", "Hot", "2026-02-10", 24000],
        ["Bob Patel", "Nimbus", "bob@nimbus.dev", "+1 415 555 0123", "Warm", "2026-02-05", 8500],
        ["Carla Rios", "Helios", "carla@helios.co", "+1 415 555 0144", "Hot", "2026-02-12", 36000],
        ["Dev Mehta", "Quanta", "dev@quanta.ai", "+1 415 555 0177", "Cold", "2026-01-22", 0],
        ["Erin Park", "Vertex", "erin@vertex.com", "+1 415 555 0192", "Hot", "2026-02-14", 18000],
        ["Faisal Khan", "Zenith", "faisal@zenith.io", "+1 415 555 0211", "Warm", "2026-02-02", 6200],
      ],
    },
  ];
  const applyTemplate = async (tpl) => {
    const g = blank();
    for (let r = 0; r < tpl.data.length && r < ROWS; r++) {
      for (let c = 0; c < tpl.data[r].length && c < COLS; c++) {
        g[r][c] = tpl.data[r][c];
      }
    }
    const d = await docsApi.create({ kind: KIND, title: tpl.title, content: g });
    setDocs([d, ...docs]);
    setActive({ ...d, content: g });
    setChat([{ role: "ai", text: `Loaded the "${tpl.title}" template with ${tpl.data.length - 1} sample rows. Ask me anything about it.` }]);
    setOverlay(null);
  };

  // ── Auto data cleaning: trim whitespace, drop full-empty rows, dedupe ──
  const cleanData = () => {
    if (!active) return;
    const before = active.content.map((row) => row.slice());
    let trimmed = 0;
    const intermediate = before.map((row) =>
      row.map((cell) => {
        if (typeof cell === "string") {
          const t = cell.replace(/\s+/g, " ").trim();
          if (t !== cell) trimmed++;
          return t;
        }
        return cell;
      })
    );
    // Drop empty rows (all cells blank).
    const nonEmpty = intermediate.filter((row) => row.some((c) => c !== "" && c != null));
    // Dedupe by row signature (preserving headers in row 0).
    const seen = new Set();
    const deduped = [];
    let duplicates = 0;
    nonEmpty.forEach((row, idx) => {
      const sig = row.map((c) => String(c).toLowerCase()).join("|");
      if (idx === 0 || !seen.has(sig)) {
        seen.add(sig);
        deduped.push(row);
      } else {
        duplicates++;
      }
    });
    // Pad back to ROWS × COLS.
    while (deduped.length < ROWS) deduped.push(Array.from({ length: COLS }, () => ""));
    const padded = deduped.map((row) => {
      const out = row.slice(0, COLS);
      while (out.length < COLS) out.push("");
      return out;
    });
    setCleanReport({
      trimmed,
      duplicates,
      beforeRows: before.filter((r) => r.some((c) => c !== "" && c != null)).length,
      afterRows: deduped.filter((r) => r.some((c) => c !== "" && c != null)).length,
      preview: padded,
    });
  };
  const applyClean = () => {
    if (!cleanReport) return;
    setActive({ ...active, content: cleanReport.preview });
    setCleanReport(null);
    setOverlay(null);
    setChat((c) => [...c, { role: "ai", text: `Cleaned: trimmed ${cleanReport.trimmed} cell(s), removed ${cleanReport.duplicates} duplicate row(s). ${cleanReport.beforeRows} → ${cleanReport.afterRows} rows.` }]);
  };

  // ── Forecasting: linear regression on a numeric column → predict next N rows ──
  const runForecast = (colIdx, n = 5) => {
    if (!meta) return null;
    const nums = columnNumbers(active.content, colIdx, meta);
    if (nums.length < 3) return { col: meta.headers[colIdx], history: nums, predicted: [], error: "Need at least 3 numeric data points." };
    // Simple linear regression: y = m*x + b
    const x = nums.map((_, i) => i);
    const n_ = nums.length;
    const sumX = x.reduce((s, v) => s + v, 0);
    const sumY = nums.reduce((s, v) => s + v, 0);
    const sumXY = x.reduce((s, v, i) => s + v * nums[i], 0);
    const sumXX = x.reduce((s, v) => s + v * v, 0);
    const m = (n_ * sumXY - sumX * sumY) / (n_ * sumXX - sumX * sumX) || 0;
    const b = (sumY - m * sumX) / n_;
    const predicted = [];
    for (let i = 0; i < n; i++) predicted.push(Number((m * (n_ + i) + b).toFixed(2)));
    return { col: meta.headers[colIdx], history: nums, predicted };
  };
  const openForecast = () => {
    if (!meta) return;
    const firstNumeric = meta.colTypes.findIndex((t) => t === "number");
    if (firstNumeric < 0) {
      setChat((c) => [...c, { role: "ai", text: "No numeric column to forecast." }]);
      return;
    }
    setForecastResult(runForecast(firstNumeric, 5));
    setOverlay("forecast");
  };
  const appendForecastToSheet = () => {
    if (!forecastResult || !forecastResult.predicted?.length || !meta) return;
    // Find the column index by header name.
    const colIdx = meta.headers.findIndex((h) => h === forecastResult.col);
    if (colIdx < 0) return;
    const next = active.content.map((row) => row.slice());
    // First empty row after the used range gets the predicted values.
    let writeRow = meta.usedRows[meta.usedRows.length - 1] + 1;
    forecastResult.predicted.forEach((v) => {
      if (writeRow >= ROWS) return;
      next[writeRow][colIdx] = v;
      // Mark a "(forecast)" tag in the first text column if one exists.
      const labelCol = meta.colTypes.findIndex((t) => t === "text");
      if (labelCol >= 0 && !next[writeRow][labelCol]) next[writeRow][labelCol] = `forecast +${forecastResult.predicted.indexOf(v) + 1}`;
      writeRow++;
    });
    setActive({ ...active, content: next });
    setOverlay(null);
  };

  // ── PDF export of the dashboard (browser print dialog) ──
  const exportDashboardPdf = () => {
    // Tag the dashboard overlay so the print stylesheet can isolate it.
    const overlayEl = document.querySelector('[data-testid="excel-dashboard-overlay"]');
    if (overlayEl) overlayEl.classList.add("print-only");
    document.body.classList.add("print-dashboard");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("print-dashboard");
      if (overlayEl) overlayEl.classList.remove("print-only");
    }, 200);
  };

  // ── Cell suggestions (when a cell is selected, surface column actions) ──
  const cellSuggestions = useMemo(() => {
    if (!selectedCell || !meta) return [];
    const { c } = selectedCell;
    if (meta.colTypes[c] !== "number") return [];
    const header = meta.headers[c];
    return [
      { label: `Sum of "${header}"`, q: `total ${header}` },
      { label: `Average of "${header}"`, q: `average ${header}` },
      { label: `Max of "${header}"`, q: `max ${header}` },
      { label: `Bar chart of "${header}"`, q: `bar chart of ${header}` },
    ];
  }, [selectedCell, meta]);

  // ── Auto-Dashboard generator: KPIs + charts for every numeric column ──
  const dashboard = useMemo(() => {
    if (!active || !meta) return null;
    const grid = active.content;
    const kpis = meta.colTypes
      .map((t, i) => t === "number" ? i : -1)
      .filter((i) => i >= 0)
      .map((i) => {
        const nums = columnNumbers(grid, i, meta);
        if (!nums.length) return null;
        const sum = nums.reduce((s, n) => s + n, 0);
        const avg = sum / nums.length;
        return {
          col: meta.headers[i],
          colIdx: i,
          sum, avg,
          min: Math.min(...nums),
          max: Math.max(...nums),
          count: nums.length,
        };
      })
      .filter(Boolean);
    const labelCol = meta.colTypes.findIndex((t) => t === "text");
    const charts = kpis.slice(0, 4).map((k) => ({
      title: k.col,
      data: meta.usedRows.slice(0, 20).map((r, idx) => ({
        name: labelCol >= 0 ? String(grid[r]?.[labelCol] || `R${idx + 1}`).slice(0, 14) : `R${idx + 1}`,
        value: parseFloat(grid[r]?.[k.colIdx]) || 0,
      })),
    }));
    return { kpis, charts };
  }, [active?.content, meta]);

  const addRow = () => {
    if (!active) return;
    const next = active.content.slice();
    next.push(Array.from({ length: COLS }, () => ""));
    if (next.length > ROWS) next.pop();
    setActive({ ...active, content: next });
  };

  return (
    <div className="h-full flex flex-col md:flex-row bg-[#0a0d18]" data-testid="app-excel">
      {/* Sheets sidebar */}
      <div
        className={`${sidebarOpen ? "flex" : "hidden md:flex"} w-full md:w-56 h-40 md:h-auto border-b md:border-b-0 md:border-r border-white/10 flex-col bg-black/30`}
      >
        <div className="p-2 flex items-center justify-between border-b border-white/8">
          <span className="text-[10px] mono uppercase tracking-widest text-white/45">// sheets</span>
          <button onClick={create} className="btn-ghost p-1.5" title="New sheet" data-testid="excel-new">
            <Plus size={12} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-1.5 py-1.5">
          {docs.length === 0 && (
            <div className="text-[11px] text-white/40 px-2 py-3">No sheets yet — click + to start.</div>
          )}
          {docs.map((d) => (
            <div
              key={d.id}
              className={`group flex items-center rounded mb-0.5 ${active?.id === d.id ? "bg-white/10" : "hover:bg-white/5"}`}
            >
              <button
                onClick={() => { setActive({ ...d, content: normalize(d.content) }); setChat([]); }}
                className="flex-1 text-left px-2.5 py-1.5 text-sm truncate"
                data-testid={`excel-sheet-${d.id}`}
              >
                {d.title}
              </button>
              <button
                onClick={() => removeSheet(d.id)}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:bg-red-400/10 p-1 mr-1 rounded transition"
                data-testid={`excel-delete-${d.id}`}
                title="Delete"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {active ? (
          <>
            {/* Toolbar */}
            <div className="flex items-center gap-1 px-3 py-1.5 border-b border-white/10 bg-black/30 flex-wrap">
              <button
                onClick={() => setSidebarOpen((s) => !s)}
                className="btn-ghost p-1 hidden md:flex"
                title={sidebarOpen ? "Collapse sheets" : "Expand sheets"}
              >
                {sidebarOpen ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
              </button>
              <input
                value={active.title}
                onChange={(e) => setActive({ ...active, title: e.target.value })}
                className="bg-transparent outline-none text-sm font-medium px-2 py-1 rounded hover:bg-white/5 min-w-[120px]"
                data-testid="excel-title"
              />
              <div className="flex-1" />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-upload"
                title="Upload .xlsx / .csv"
              >
                <Upload size={11} /> Upload
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                hidden
                onChange={onUpload}
                data-testid="excel-file-input"
              />
              <button
                onClick={saveNow}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-save"
                title="Save (Ctrl+S)"
              >
                <Save size={11} /> Save
              </button>
              <button
                onClick={() => {
                  setSaveAsName(active?.title ? `${active.title} (copy)` : "Untitled Sheet");
                  setOverlay("saveas");
                }}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-save-as"
                title="Save As… (Ctrl+Shift+S)"
              >
                <FileText size={11} /> Save As
              </button>
              <button
                onClick={downloadXlsx}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-download"
                title="Download .xlsx"
              >
                <Download size={11} /> Download
              </button>
              <button
                onClick={addRow}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-add-row"
                title="Add row"
              >
                <Plus size={11} /> Row
              </button>
              <button
                onClick={() => setOverlay("templates")}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-templates"
                title="Start from a template"
              >
                <LayoutTemplate size={11} /> Templates
              </button>
              <button
                onClick={() => setOverlay("formula")}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-formula"
                title="Plain-English formula generator"
              >
                <FunctionSquare size={11} /> Formula
              </button>
              <button
                onClick={() => { cleanData(); setOverlay("clean"); }}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-clean"
                title="Auto-clean data (trim + dedupe + drop empty rows)"
              >
                <Brush size={11} /> Clean
              </button>
              <button
                onClick={openForecast}
                className="btn-ghost text-[11px] flex items-center gap-1 px-2"
                data-testid="excel-forecast"
                title="Forecast next 5 rows of the first numeric column"
              >
                <TrendingUp size={11} /> Forecast
              </button>
              <button
                onClick={() => setDashOpen(true)}
                className="btn-accent text-[11px] flex items-center gap-1 px-2"
                style={{ borderColor: "rgba(167,139,250,0.5)" }}
                data-testid="excel-dashboard"
                title="Auto-generate dashboard"
              >
                <LayoutDashboard size={11} /> Dashboard
              </button>
              <div
                className="hidden lg:flex items-center gap-1 text-[10px] mono ml-2"
                data-testid="excel-save-status"
                title={lastSavedAt ? `Last saved ${lastSavedAt.toLocaleTimeString()}` : ""}
                style={{
                  color: saveState === "error" ? "#f87171" :
                         saveState === "saving" ? "#fbbf24" :
                         saveState === "saved" ? "rgba(52,211,153,0.85)" :
                         "rgba(255,255,255,0.4)",
                }}
              >
                {saveState === "saving" ? (
                  <><Loader2 size={10} className="animate-spin" /> Saving…</>
                ) : saveState === "saved" ? (
                  <><Check size={10} /> Saved{lastSavedAt ? ` · ${lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}</>
                ) : saveState === "error" ? (
                  <><X size={10} /> Save failed</>
                ) : (
                  <><Save size={10} /> Unsaved</>
                )}
              </div>
            </div>

            {/* Grid + AI panel */}
            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
              {/* Grid */}
              <div className="flex-1 overflow-auto bg-black/20 min-h-[280px]">
                <table className="mono text-xs border-collapse">
                  <thead className="sticky top-0 z-10 bg-[#0e1320]">
                    <tr>
                      <th className="w-10 text-[#8b5cf6]/55 text-[10px] font-normal border-r border-white/10">#</th>
                      {Array.from({ length: COLS }).map((_, c) => {
                        const isHeader = meta?.hasHeaders;
                        const headerText = isHeader ? active.content[0]?.[c] : null;
                        return (
                          <th
                            key={c}
                            className="excel-cell text-center text-white/55 text-[10px] font-normal border border-white/8"
                            style={{ minWidth: 88, background: selectedCell?.c === c ? "rgba(139,92,246,0.10)" : undefined }}
                            title={headerText ? `${colLetter(c)} · ${headerText}` : colLetter(c)}
                          >
                            <div className="flex items-center justify-center gap-1">
                              <span>{colLetter(c)}</span>
                              {meta?.colTypes[c] === "number" && <Sigma size={9} className="text-[#22c55e]/70" />}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {active.content.map((row, r) => (
                      <tr key={r}>
                        <td className="text-center text-white/45 text-[10px] pr-1 border-r border-white/10 sticky left-0 bg-[#0e1320]/95">{r + 1}</td>
                        {row.map((cell, c) => {
                          const isSelected = selectedCell?.r === r && selectedCell?.c === c;
                          const isHeaderRow = meta?.hasHeaders && r === 0;
                          return (
                            <td
                              key={c}
                              className="border border-white/8"
                              style={{
                                background: isSelected ? "rgba(167,139,250,0.18)" : isHeaderRow ? "rgba(139,92,246,0.06)" : undefined,
                              }}
                            >
                              <input
                                data-testid={`excel-cell-${colLetter(c)}${r + 1}`}
                                value={cell || ""}
                                onChange={(e) => setCell(r, c, e.target.value)}
                                onFocus={() => setSelectedCell({ r, c })}
                                className="excel-cell w-full bg-transparent outline-none px-1.5 py-1"
                                style={{ fontWeight: isHeaderRow ? 600 : 400, color: isHeaderRow ? "#c7c1ff" : "rgba(255,255,255,0.92)" }}
                                title={typeof cell === "string" && cell.startsWith("=") ? `= ${evalCell(cell)}` : ""}
                              />
                              {typeof cell === "string" && cell.startsWith("=") && (
                                <div className="text-[10px] text-[#ff7a2e] px-1.5 pb-0.5">{String(evalCell(cell))}</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* AI Brain panel (chat) */}
              <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-white/10 bg-black/40 flex flex-col min-h-[260px]">
                <div className="px-3 py-2 border-b border-white/10 flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest text-[#a78bfa]">
                  <Sparkles size={11} color="#ff7a2e" /> // ai brain
                </div>
                {/* Suggestions for selected cell */}
                {cellSuggestions.length > 0 && (
                  <div className="px-3 py-2 border-b border-white/8 space-y-1">
                    <div className="mono text-[9px] uppercase tracking-widest text-white/45">
                      cell <span className="text-[#a78bfa]">{selectedCell ? `${colLetter(selectedCell.c)}${selectedCell.r + 1}` : ""}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {cellSuggestions.map((s, i) => (
                        <button
                          key={i}
                          data-testid={`excel-suggest-${i}`}
                          onClick={() => sendChat(s.q)}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 hover:bg-white/10 text-white/80"
                          title={s.q}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Chat history */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="excel-chat">
                  {chat.length === 0 && (
                    <div className="text-[11px] text-white/45">
                      Ask anything about your data —<br />
                      <span className="mono text-[10px] text-white/35">
                        &quot;total sales&quot;<br />
                        &quot;bar chart of revenue&quot;<br />
                        &quot;average price&quot;
                      </span>
                    </div>
                  )}
                  {chat.map((m, i) => (
                    <div
                      key={i}
                      className={`text-[11.5px] rounded p-2 ${m.role === "user" ? "bg-white/8 text-white/85" : "bg-[#a78bfa]/8 border border-[#a78bfa]/20 text-white/90"}`}
                    >
                      <div className="mono text-[8.5px] uppercase tracking-widest mb-1" style={{ color: m.role === "user" ? "rgba(255,255,255,0.45)" : "#a78bfa" }}>
                        // {m.role === "user" ? "you" : "ai"}
                      </div>
                      <div className="whitespace-pre-wrap leading-snug">{m.text}</div>
                      {m.chart && <InlineChart chart={m.chart} />}
                    </div>
                  ))}
                  {aiBusy && (
                    <div className="text-[11px] flex items-center gap-1.5 text-[#a78bfa]/80 mono">
                      <Loader2 size={11} className="animate-spin" /> thinking…
                    </div>
                  )}
                </div>
                <div className="p-2 border-t border-white/10">
                  <form
                    onSubmit={(e) => { e.preventDefault(); sendChat(); }}
                    className="flex gap-1"
                  >
                    <div className="flex-1 relative">
                      <input
                        value={aiInput}
                        onChange={(e) => setAiInput(e.target.value)}
                        data-testid="excel-ai-input"
                        placeholder={listening ? "Listening…" : "Ask the data…"}
                        disabled={aiBusy}
                        className="w-full bg-white/5 border border-white/10 rounded pl-2 pr-8 py-1.5 text-xs outline-none focus:border-[#a78bfa]/60"
                      />
                      <button
                        type="button"
                        data-testid="excel-ai-voice"
                        onClick={toggleVoice}
                        title={listening ? "Stop dictating" : "Voice input"}
                        className="absolute top-1/2 right-1.5 -translate-y-1/2 p-1 rounded-full transition"
                        style={{
                          background: listening ? "rgba(248,113,113,0.20)" : "transparent",
                          border: `1px solid ${listening ? "rgba(248,113,113,0.6)" : "rgba(167,139,250,0.45)"}`,
                          color: listening ? "#f87171" : "#c7c1ff",
                        }}
                      >
                        {listening ? <MicOff size={10} /> : <Mic size={10} />}
                      </button>
                    </div>
                    <button
                      data-testid="excel-ai-send"
                      type="submit"
                      disabled={aiBusy || !aiInput.trim()}
                      className="btn-accent text-xs flex items-center gap-1 px-2"
                    >
                      <Sparkles size={11} />
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-white/55 text-sm p-8 text-center">
            <Calculator size={28} className="text-[#a78bfa]/60" />
            <div>No sheet open yet</div>
            <div className="flex gap-2">
              <button onClick={create} className="btn-accent text-xs flex items-center gap-1" data-testid="excel-empty-new">
                <Plus size={11} /> New sheet
              </button>
              <button onClick={() => fileInputRef.current?.click()} className="btn-ghost text-xs flex items-center gap-1">
                <Upload size={11} /> Upload .xlsx
              </button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onUpload} />
            </div>
          </div>
        )}
      </div>

      {/* Dashboard overlay */}
      {dashOpen && (
        <div
          data-testid="excel-dashboard-overlay"
          className="absolute inset-0 z-20 bg-[#04060e]/90 flex flex-col"
          style={{ backdropFilter: "blur(8px)" }}
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Wand2 size={14} className="text-[#a78bfa]" />
              <div>
                <div className="text-sm font-medium">Auto Dashboard</div>
                <div className="mono text-[9px] uppercase tracking-widest text-white/45">
                  // {active?.title} · generated from headers & numeric columns
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="excel-dashboard-pdf"
                onClick={exportDashboardPdf}
                className="btn-ghost text-xs px-2 flex items-center gap-1"
              >
                <FileText size={11} /> Export PDF
              </button>
              <button
                data-testid="excel-dashboard-close"
                onClick={() => setDashOpen(false)}
                className="btn-ghost text-xs px-2"
              >
                Close
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            {!dashboard || dashboard.kpis.length === 0 ? (
              <div className="text-white/55 text-sm text-center mt-12">
                Add at least one column of numbers (e.g. a sales total) and a header row to generate a dashboard.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {dashboard.kpis.map((k) => (
                    <div
                      key={k.col}
                      data-testid={`excel-kpi-${k.col}`}
                      className="rounded-xl border border-[#a78bfa]/25 bg-[#a78bfa]/6 p-3"
                    >
                      <div className="mono text-[9px] uppercase tracking-widest text-[#a78bfa]/80 truncate">{k.col}</div>
                      <div className="text-2xl font-semibold mt-1 text-white">
                        {k.sum.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-[10px] text-white/55 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>avg {k.avg.toFixed(2)}</span>
                        <span>min {k.min}</span>
                        <span>max {k.max}</span>
                        <span>{k.count} rows</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-5">
                  {dashboard.charts.map((c, i) => (
                    <div
                      key={c.title + i}
                      data-testid={`excel-chart-${c.title}`}
                      className="rounded-xl border border-white/10 bg-black/30 p-3"
                      style={{ height: 280 }}
                    >
                      <div className="mono text-[9px] uppercase tracking-widest text-white/55 mb-1">// {c.title}</div>
                      <ResponsiveContainer width="100%" height="92%">
                        <BarChart data={c.data}>
                          <XAxis dataKey="name" stroke="rgba(255,255,255,0.45)" fontSize={10} />
                          <YAxis stroke="rgba(255,255,255,0.45)" fontSize={10} />
                          <Tooltip
                            contentStyle={{ background: "#ffffff", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 8, fontSize: 11 }}
                          />
                          <Bar dataKey="value" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {/* Save As overlay — MS Office style: choose a name + destination/format */}
      {overlay === "saveas" && (
        <OverlayShell title="Save As" icon={Save} onClose={() => { setOverlay(null); setSaveAsName(""); }}>
          <div className="space-y-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1.5">// file name</div>
              <input
                data-testid="excel-saveas-name"
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                placeholder="My new sheet"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); saveAsNewSheet(saveAsName); }
                }}
                className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-[#a78bfa]/60 mono"
              />
              <div className="mt-1 text-[10px] mono uppercase tracking-widest text-white/40">// destination</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                data-testid="excel-saveas-cloud"
                onClick={() => saveAsNewSheet(saveAsName)}
                disabled={!saveAsName.trim()}
                className="flex flex-col items-start gap-1.5 p-3 text-left transition hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                style={{
                  background: "linear-gradient(160deg, rgba(167,139,250,0.18), rgba(139,92,246,0.08))",
                  border: "1px solid rgba(167,139,250,0.45)",
                  borderRadius: 4,
                }}
              >
                <LayoutDashboard size={14} className="text-[#a78bfa]" />
                <div className="text-[11px] font-semibold uppercase tracking-wider mono text-white">HEXA Cloud</div>
                <div className="text-[10px] text-white/55 mono">New sheet · auto-synced</div>
              </button>

              <button
                data-testid="excel-saveas-xlsx"
                onClick={() => {
                  const old = active.title;
                  setActive({ ...active, title: saveAsName || old });
                  setTimeout(() => {
                    downloadXlsx();
                    setActive((a) => ({ ...a, title: old }));
                    setOverlay(null);
                    setSaveAsName("");
                  }, 50);
                }}
                disabled={!saveAsName.trim()}
                className="flex flex-col items-start gap-1.5 p-3 text-left transition hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                style={{
                  background: "linear-gradient(160deg, rgba(34,197,94,0.18), rgba(34,197,94,0.06))",
                  border: "1px solid rgba(34,197,94,0.45)",
                  borderRadius: 4,
                }}
              >
                <Download size={14} className="text-[#22c55e]" />
                <div className="text-[11px] font-semibold uppercase tracking-wider mono text-white">Download .xlsx</div>
                <div className="text-[10px] text-white/55 mono">Excel workbook</div>
              </button>

              <button
                data-testid="excel-saveas-csv"
                onClick={() => {
                  const old = active.title;
                  setActive({ ...active, title: saveAsName || old });
                  setTimeout(() => {
                    downloadCsv();
                    setActive((a) => ({ ...a, title: old }));
                    setOverlay(null);
                    setSaveAsName("");
                  }, 50);
                }}
                disabled={!saveAsName.trim()}
                className="flex flex-col items-start gap-1.5 p-3 text-left transition hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                style={{
                  background: "linear-gradient(160deg, rgba(59,130,246,0.18), rgba(59,130,246,0.06))",
                  border: "1px solid rgba(59,130,246,0.45)",
                  borderRadius: 4,
                }}
              >
                <FileText size={14} className="text-[#60a5fa]" />
                <div className="text-[11px] font-semibold uppercase tracking-wider mono text-white">Download .csv</div>
                <div className="text-[10px] text-white/55 mono">Comma separated</div>
              </button>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="text-[10px] mono text-white/45 uppercase tracking-widest">
                // shortcut: ctrl + shift + s
              </div>
              <button
                onClick={() => { setOverlay(null); setSaveAsName(""); }}
                className="btn-ghost text-xs"
              >
                Cancel
              </button>
            </div>
          </div>
        </OverlayShell>
      )}

      {/* Formula generator overlay */}
      {overlay === "formula" && (
        <OverlayShell title="Plain-English Formula" icon={FunctionSquare} onClose={() => { setOverlay(null); setFormulaResult(""); setFormulaInput(""); }}>
          <div className="space-y-3">
            <div className="mono text-[10px] uppercase tracking-widest text-white/45">// describe what you want</div>
            <textarea
              data-testid="excel-formula-input"
              rows={3}
              value={formulaInput}
              onChange={(e) => setFormulaInput(e.target.value)}
              placeholder='e.g. "If sales in B > 1000 show High else Low" or "Sum revenue where region equals US"'
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-[#a78bfa]/60"
            />
            <button
              data-testid="excel-formula-run"
              disabled={formulaBusy || !formulaInput.trim()}
              onClick={runFormulaGen}
              className="btn-accent text-xs flex items-center gap-1.5 px-3 py-1.5"
            >
              {formulaBusy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} Generate formula
            </button>
            {formulaResult && (
              <div data-testid="excel-formula-result" className="mt-2 bg-black/30 border border-[#a78bfa]/35 rounded p-3">
                <div className="mono text-[10px] uppercase tracking-widest text-[#a78bfa]/85 mb-1">// formula</div>
                <div className="mono text-sm break-all text-[#22c55e] select-all">{formulaResult}</div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => navigator.clipboard?.writeText(formulaResult)}
                    className="btn-ghost text-[11px] flex items-center gap-1"
                  >
                    Copy
                  </button>
                  <button
                    data-testid="excel-formula-apply"
                    onClick={applyFormulaToSelected}
                    disabled={!selectedCell || !formulaResult.startsWith("=")}
                    className="btn-accent text-[11px] flex items-center gap-1"
                  >
                    <Check size={11} /> Apply to {selectedCell ? `${colLetter(selectedCell.c)}${selectedCell.r + 1}` : "selected cell"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </OverlayShell>
      )}

      {/* Templates gallery overlay */}
      {overlay === "templates" && (
        <OverlayShell title="Templates" icon={LayoutTemplate} onClose={() => setOverlay(null)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {TEMPLATES.map((tpl) => {
              const Ic = tpl.icon;
              return (
                <button
                  key={tpl.id}
                  data-testid={`excel-template-${tpl.id}`}
                  onClick={() => applyTemplate(tpl)}
                  className="text-left rounded-xl border border-white/10 bg-white/4 hover:bg-white/8 p-4 transition flex flex-col gap-2"
                  style={{ borderColor: `${tpl.tint}55` }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center justify-center"
                      style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: `${tpl.tint}22`,
                        border: `1px solid ${tpl.tint}66`,
                        color: tpl.tint,
                      }}
                    >
                      <Ic size={16} />
                    </span>
                    <div className="text-sm font-medium text-white">{tpl.title}</div>
                  </div>
                  <div className="text-[11px] text-white/55">{tpl.desc}</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-white/35 mt-auto pt-1">
                    {tpl.data[0].length} columns · {tpl.data.length - 1} rows
                  </div>
                </button>
              );
            })}
          </div>
        </OverlayShell>
      )}

      {/* Clean overlay */}
      {overlay === "clean" && cleanReport && (
        <OverlayShell title="Auto Clean" icon={Brush} onClose={() => { setOverlay(null); setCleanReport(null); }}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <CleanStat label="Trimmed cells" value={cleanReport.trimmed} tint="#22d3ee" />
              <CleanStat label="Duplicates" value={cleanReport.duplicates} tint="#f59e0b" />
              <CleanStat label="Before" value={cleanReport.beforeRows} tint="#94a3b8" suffix=" rows" />
              <CleanStat label="After" value={cleanReport.afterRows} tint="#22c55e" suffix=" rows" />
            </div>
            <div className="text-[11px] text-white/55">
              Trims extra whitespace, drops blank rows, and removes exact-duplicate rows (case-insensitive). Header row preserved.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { setOverlay(null); setCleanReport(null); }} className="btn-ghost text-xs">Cancel</button>
              <button
                data-testid="excel-clean-apply"
                onClick={applyClean}
                disabled={cleanReport.trimmed === 0 && cleanReport.duplicates === 0 && cleanReport.beforeRows === cleanReport.afterRows}
                className="btn-accent text-xs flex items-center gap-1"
              >
                <Check size={11} /> Apply cleanup
              </button>
            </div>
          </div>
        </OverlayShell>
      )}

      {/* Forecast overlay */}
      {overlay === "forecast" && forecastResult && (
        <OverlayShell title="Forecast" icon={TrendingUp} onClose={() => setOverlay(null)}>
          <div className="space-y-3">
            <div className="mono text-[10px] uppercase tracking-widest text-white/45">
              // next 5 values · column <span className="text-[#a78bfa]">{forecastResult.col}</span> · linear regression
            </div>
            {forecastResult.error ? (
              <div className="text-red-400 text-sm">{forecastResult.error}</div>
            ) : (
              <>
                <div style={{ height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={[
                      ...forecastResult.history.map((v, i) => ({ name: `r${i + 1}`, history: v })),
                      ...forecastResult.predicted.map((v, i) => ({ name: `+${i + 1}`, predicted: v })),
                    ]}>
                      <XAxis dataKey="name" stroke="rgba(255,255,255,0.45)" fontSize={10} />
                      <YAxis stroke="rgba(255,255,255,0.45)" fontSize={10} />
                      <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 8, fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Line dataKey="history" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2 }} />
                      <Line dataKey="predicted" stroke="#22c55e" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {forecastResult.predicted.map((v, i) => (
                    <div key={i} className="bg-[#22c55e]/15 border border-[#22c55e]/40 rounded px-2 py-1 mono text-[11px] text-[#86efac]">
                      +{i + 1}: {v.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setOverlay(null)} className="btn-ghost text-xs">Close</button>
                  <button
                    data-testid="excel-forecast-append"
                    onClick={appendForecastToSheet}
                    className="btn-accent text-xs flex items-center gap-1"
                  >
                    <Check size={11} /> Append to sheet
                  </button>
                </div>
              </>
            )}
          </div>
        </OverlayShell>
      )}
    </div>
  );
}

// Generic centered overlay used by the Formula / Templates / Clean / Forecast modals.
function OverlayShell({ title, icon: Ic, onClose, children }) {
  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center p-4"
      style={{ background: "rgba(2,3,8,0.7)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="glass rounded-xl w-full max-w-xl max-h-[85%] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Ic size={14} className="text-[#a78bfa]" />
            <div className="text-sm font-medium">{title}</div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={12} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );
}

function CleanStat({ label, value, tint, suffix = "" }) {
  return (
    <div
      className="rounded-lg p-2 text-center"
      style={{ background: `${tint}11`, border: `1px solid ${tint}44` }}
    >
      <div className="mono text-[9px] uppercase tracking-widest" style={{ color: `${tint}cc` }}>{label}</div>
      <div className="text-lg font-semibold mt-0.5 text-white">{value}{suffix}</div>
    </div>
  );
}

// ───────────────────────────────────── Inline chart for chat replies ─────────────────────────────────────
function InlineChart({ chart }) {
  const data = chart.data.slice(0, 24);
  if (chart.type === "pie") {
    return (
      <div style={{ height: 180 }} className="mt-2">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" outerRadius={70} label>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }
  if (chart.type === "line") {
    return (
      <div style={{ height: 160 }} className="mt-2">
        <ResponsiveContainer>
          <LineChart data={data}>
            <XAxis dataKey="name" stroke="rgba(255,255,255,0.45)" fontSize={9} />
            <YAxis stroke="rgba(255,255,255,0.45)" fontSize={9} />
            <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 8, fontSize: 11 }} />
            <Line type="monotone" dataKey="value" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }
  return (
    <div style={{ height: 160 }} className="mt-2">
      <ResponsiveContainer>
        <BarChart data={data}>
          <XAxis dataKey="name" stroke="rgba(255,255,255,0.45)" fontSize={9} />
          <YAxis stroke="rgba(255,255,255,0.45)" fontSize={9} />
          <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 8, fontSize: 11 }} />
          <Bar dataKey="value" fill="#a78bfa" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
